<template>
  <div v-if="previewCard" v-bind="attrs" class="relative w-56 max-w-full overflow-hidden rounded-lg border border-border bg-background text-foreground shadow-sm">
    <div v-if="canPreviewFile" class="relative">
      <div class="flex h-32 items-center justify-center overflow-hidden bg-muted" aria-hidden="true">
        <img v-if="previewKind === 'image'" :src="imageThumbnailUrl" :alt="fileName" class="h-full w-full object-cover">
        <video v-else-if="previewKind === 'video'" :src="resolvedOpenUrl" class="h-full w-full object-cover" muted playsinline preload="metadata" />
        <iframe v-else-if="previewKind === 'pdf'" :src="pdfThumbnailUrl" class="pointer-events-none h-full w-full border-0 bg-white" tabindex="-1" title="PDF thumbnail" />
        <span v-else class="flex flex-col items-center gap-2 text-muted-foreground">
          <component :is="resolvedIconComponent" class="h-10 w-10" />
          <span class="text-xs font-semibold uppercase tracking-wide">{{ previewTypeLabel }}</span>
        </span>
      </div>

      <button class="group absolute inset-0 flex flex-col justify-end text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" type="button" :aria-label="`Preview ${fileName}`" @click.stop="openPreview">
        <span class="absolute inset-0 bg-black/0 transition group-hover:bg-black/10" />
        <span class="relative flex items-center gap-2 border-t border-border bg-background/95 px-3 py-2 backdrop-blur-sm">
          <component :is="resolvedIconComponent" class="h-4 w-4 shrink-0 text-muted-foreground" />
          <span class="min-w-0 flex-1 truncate text-xs font-medium" :title="fileName">{{ fileName }}</span>
          <Eye class="h-4 w-4 shrink-0 text-muted-foreground" />
        </span>
      </button>
    </div>

    <a v-else-if="resolvedOpenUrl" class="group flex flex-col text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" :href="resolvedOpenUrl" :download="fileName" :aria-label="`Download ${fileName}`" @click.stop>
      <span class="flex h-24 flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
        <component :is="resolvedIconComponent" class="h-9 w-9" />
        <span class="text-xs font-semibold uppercase tracking-wide">{{ previewTypeLabel }}</span>
      </span>
      <span class="flex items-center gap-2 border-t border-border px-3 py-2 transition group-hover:bg-muted/50">
        <span class="min-w-0 flex-1 truncate text-xs font-medium" :title="fileName">Download {{ fileName }}</span>
        <Download class="h-4 w-4 shrink-0 text-muted-foreground" />
      </span>
    </a>

    <div v-else class="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground">
      <component :is="resolvedIconComponent" class="h-5 w-5 shrink-0" />
      <span class="min-w-0 flex-1 truncate" :title="fileName">{{ fileName }}</span>
    </div>

    <a v-if="canPreviewFile && resolvedOpenUrl" class="absolute right-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm transition hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" :href="resolvedOpenUrl" :download="fileName" :aria-label="`Download ${fileName}`" @click.stop>
      <Download class="h-4 w-4" />
    </a>

    <button v-if="deletable" class="absolute top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-100 text-red-600 shadow-sm transition hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-60" :class="canPreviewFile && resolvedOpenUrl ? 'right-11' : 'right-2'" type="button" :disabled="isDeleting" aria-label="Delete uploaded file" @click.stop="handleDelete">
      <Trash2 class="h-4 w-4" />
    </button>
  </div>

  <div v-else v-bind="attrs" class="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
    <button
      v-if="canPreviewFile"
      class="inline-flex min-w-0 items-center gap-2 rounded-full text-left transition hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
      type="button"
      :aria-label="`Preview ${fileName}`"
      @click.stop="openPreview"
    >
      <component :is="resolvedIconComponent" class="h-4 w-4 shrink-0 text-slate-500" />

      <span class="max-w-56 truncate" :title="fileName">
        {{ fileName }}
      </span>
    </button>

    <template v-else>
      <component :is="resolvedIconComponent" class="h-4 w-4 shrink-0 text-slate-500" />

      <span class="max-w-56 truncate" :title="fileName">
        {{ fileName }}
      </span>
    </template>

    <a
      v-if="resolvedOpenUrl"
      class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-600 transition hover:bg-slate-300"
      :href="resolvedOpenUrl"
      :download="fileName"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download uploaded file"
      @click.stop
    >
      <Download class="h-3.5 w-3.5" />
    </a>

    <button
      v-if="deletable"
      class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 transition hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-60"
      type="button"
      :disabled="isDeleting"
      aria-label="Delete uploaded file"
      @click.stop="handleDelete"
    >
      <Trash2 class="h-3.5 w-3.5" />
    </button>
  </div>

  <Dialog :open="previewOpen" @update:open="previewOpen = $event">
    <DialogContent class="max-h-[90vh] max-w-4xl gap-4 overflow-hidden p-0">
      <DialogHeader class="border-b border-slate-200 px-4 py-3 sm:px-6 sm:py-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div class="min-w-0 flex-1">
            <DialogTitle class="truncate text-base font-semibold text-slate-900">
              {{ fileName }}
            </DialogTitle>
            <DialogDescription class="text-sm text-slate-500">
              Preview available for supported file types. Use download to save the original file.
            </DialogDescription>
          </div>

          <Button class="w-full sm:mr-6 sm:w-auto" as="a" size="sm" :href="resolvedOpenUrl" :download="fileName" target="_blank" rel="noopener noreferrer">
            <Download class="h-4 w-4" />
            Download
          </Button>
        </div>
      </DialogHeader>

      <div class="flex max-h-[calc(90vh-7rem)] min-h-0 flex-col gap-3 overflow-auto px-4 py-3 sm:max-h-[calc(90vh-8rem)] sm:gap-4 sm:px-6 sm:py-4">
        <div v-if="previewKind === 'image'" class="flex justify-center">
          <img :src="resolvedOpenUrl" :alt="fileName" class="max-h-[65vh] w-auto max-w-full rounded-md object-contain">
        </div>

        <video v-else-if="previewKind === 'video'" :src="resolvedOpenUrl" controls class="max-h-[65vh] w-full rounded-md bg-black" />

        <div v-else-if="previewKind === 'audio'" class="rounded-md border border-slate-200 bg-slate-50 p-6">
          <audio :src="resolvedOpenUrl" controls class="w-full">
            Your browser does not support audio preview.
          </audio>
        </div>

        <div v-else-if="previewKind === 'docx'" class="rounded-md border border-slate-200 bg-slate-100 p-2 sm:p-4">
          <p v-if="isDocxPreviewLoading" class="text-sm text-slate-500">
            Loading DOCX preview...
          </p>

          <p v-if="docxPreviewErrorMessage" class="text-sm text-red-600">
            {{ docxPreviewErrorMessage }}
          </p>

          <div ref="docxPreviewContainer" class="docx-preview-host overflow-x-auto" :class="{ 'hidden': docxPreviewErrorMessage !== '' }" />
        </div>

        <iframe v-else-if="previewKind === 'text'" :src="resolvedOpenUrl" sandbox class="h-[65vh] w-full rounded-md border border-slate-200 bg-white" title="Text file preview" />

        <iframe
          v-else-if="previewKind === 'pdf'"
          :src="resolvedOpenUrl"
          class="h-[65vh] w-full rounded-md border border-slate-200"
          title="Uploaded file preview"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup>
import axios from 'axios'
import { computed, nextTick, ref, useAttrs, watch } from 'vue'
import {
  AudioLines,
  Download,
  Eye,
  File,
  FileArchive,
  FileImage,
  FileText,
  FileVideoCamera,
  Trash2,
} from 'lucide-vue-next'
import { Button } from '../../../../../../resources/js/components/ui/button/index.js'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../../resources/js/components/ui/dialog/index.js'

defineOptions({
  inheritAttrs: false,
})

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  deletable: {
    type: Boolean,
    default: true,
  },
  autoDelete: {
    type: Boolean,
    default: true,
  },
  deleteUrl: {
    type: String,
    default: null,
  },
  previewCard: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['delete', 'deleted', 'error', 'delete-start', 'delete-finish'])

const attrs = useAttrs()
const isDeleting = ref(false)
const previewOpen = ref(false)
const docxPreviewContainer = ref(null)
const isDocxPreviewLoading = ref(false)
const docxPreviewErrorMessage = ref('')
let docxPreviewRequestId = 0

const fileName = computed(() => {
  const candidate = props.file?.filename ?? props.file?.name ?? 'Unnamed file'

  if (typeof candidate !== 'string' || candidate.trim() === '') {
    return 'Unnamed file'
  }

  return candidate
})

const fileExtension = computed(() => {
  return String(props.file?.extension ?? '').toLowerCase()
})

const fileMimeType = computed(() => {
  return String(props.file?.mime_type ?? props.file?.mimeType ?? '').toLowerCase()
})

const fileIconName = computed(() => {
  return String(props.file?.icon ?? '').toLowerCase()
})

const resolvedIconComponent = computed(() => {
  if (fileMimeType.value.startsWith('image/') || fileIconName.value.includes('image')) {
    return FileImage
  }

  if (fileMimeType.value.startsWith('video/') || fileIconName.value.includes('video')) {
    return FileVideoCamera
  }

  if (fileMimeType.value.startsWith('audio/') || fileIconName.value.includes('audio')) {
    return AudioLines
  }

  if (['zip', 'rar', '7zip', 'gz', 'tar'].includes(fileExtension.value) || fileIconName.value.includes('archive')) {
    return FileArchive
  }

  if (fileMimeType.value.startsWith('text/') || ['csv', 'doc', 'docx', 'pdf', 'txt', 'rtf'].includes(fileExtension.value)) {
    return FileText
  }

  return File
})

const resolvedDeleteUrl = computed(() => {
  if (props.deleteUrl !== null && props.deleteUrl !== '') {
    return props.deleteUrl
  }

  const fileId = props.file?.id
  if (fileId === null || fileId === undefined || fileId === '') {
    return null
  }

  return `/uploads/${fileId}`
})

const resolvedOpenUrl = computed(() => {
  const secureUrl = props.file?.url_secret
  if (typeof secureUrl === 'string' && secureUrl !== '') {
    return secureUrl
  }

  const publicUrl = props.file?.url
  if (typeof publicUrl === 'string' && publicUrl !== '') {
    return publicUrl
  }

  return null
})

const imageThumbnailUrl = computed(() => {
  const thumbnailCandidates = [props.file?.url_small, props.file?.url_medium, props.file?.url_large, resolvedOpenUrl.value]

  return thumbnailCandidates.find((candidate) => typeof candidate === 'string' && candidate !== '') ?? null
})

const pdfThumbnailUrl = computed(() => {
  if (resolvedOpenUrl.value === null) {
    return null
  }

  const [urlWithoutFragment] = resolvedOpenUrl.value.split('#')

  return `${urlWithoutFragment}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`
})

const previewTypeLabel = computed(() => {
  return fileExtension.value || previewKind.value || 'file'
})

const previewKind = computed(() => {
  if (resolvedOpenUrl.value === null) {
    return null
  }

  if (fileMimeType.value.startsWith('image/')) {
    return 'image'
  }

  if (fileMimeType.value.startsWith('video/')) {
    return 'video'
  }

  if (fileMimeType.value.startsWith('audio/')) {
    return 'audio'
  }

  if (fileMimeType.value.startsWith('text/') || ['application/json', 'application/xml'].includes(fileMimeType.value)) {
    return 'text'
  }

  if (fileMimeType.value === DOCX_MIME_TYPE || fileExtension.value === 'docx') {
    return 'docx'
  }

  if (fileMimeType.value === 'application/pdf' || fileExtension.value === 'pdf') {
    return 'pdf'
  }

  return null
})

const canPreviewFile = computed(() => {
  return previewKind.value !== null
})

const openPreview = () => {
  if (!canPreviewFile.value) {
    return
  }

  previewOpen.value = true
}

const clearDocxPreview = () => {
  if (docxPreviewContainer.value instanceof HTMLElement) {
    docxPreviewContainer.value.innerHTML = ''
  }
}

const resolveDocxPreviewErrorMessage = (error) => {
  if (error instanceof Error && error.message.trim() !== '') {
    return error.message
  }

  return 'Unable to preview this DOCX file.'
}

const renderDocxPreview = async () => {
  if (previewKind.value !== 'docx' || resolvedOpenUrl.value === null) {
    return
  }

  await nextTick()

  if (!(docxPreviewContainer.value instanceof HTMLElement)) {
    return
  }

  const currentRequestId = ++docxPreviewRequestId
  isDocxPreviewLoading.value = true
  docxPreviewErrorMessage.value = ''
  clearDocxPreview()

  try {
    const response = await fetch(resolvedOpenUrl.value, {
      credentials: 'same-origin',
    })

    if (!response.ok) {
      throw new Error('Unable to load this DOCX file for preview.')
    }

    const blob = await response.blob()
    const { renderAsync } = await import('docx-preview')

    if (currentRequestId !== docxPreviewRequestId || !previewOpen.value || previewKind.value !== 'docx') {
      return
    }

    clearDocxPreview()

    await renderAsync(blob, docxPreviewContainer.value, docxPreviewContainer.value, {
      breakPages: false,
      className: 'docx-preview',
      inWrapper: true,
    })
  } catch (error) {
    if (currentRequestId !== docxPreviewRequestId) {
      return
    }

    docxPreviewErrorMessage.value = resolveDocxPreviewErrorMessage(error)
    clearDocxPreview()
  } finally {
    if (currentRequestId === docxPreviewRequestId) {
      isDocxPreviewLoading.value = false
    }
  }
}

watch([previewOpen, previewKind], async ([isOpen, activePreviewKind]) => {
  if (!isOpen || activePreviewKind !== 'docx') {
    docxPreviewErrorMessage.value = ''
    isDocxPreviewLoading.value = false
    clearDocxPreview()

    return
  }

  await renderDocxPreview()
})

const handleDelete = async () => {
  emit('delete', props.file)

  if (!props.autoDelete || resolvedDeleteUrl.value === null) {
    return
  }

  if (isDeleting.value) {
    return
  }

  isDeleting.value = true
  emit('delete-start', props.file)

  try {
    await axios.delete(resolvedDeleteUrl.value)
    emit('deleted', props.file)
  } catch (error) {
    emit('error', error)
  } finally {
    isDeleting.value = false
    emit('delete-finish', props.file)
  }
}
</script>

<style scoped>
.docx-preview-host :deep(.docx-preview-wrapper) {
  background-color: transparent;
  padding: 0;
}

.docx-preview-host :deep(.docx-preview) {
  margin: 0 auto;
}
</style>
